import React from "react";
import {
  TextField,
  TextFieldProps,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

export interface StyledTextFieldProps extends Omit<TextFieldProps, "variant"> {
  /** Icon to display at the start of the input */
  startIcon?: React.ReactElement;
  /** Icon to display at the end of the input */
  endIcon?: React.ReactElement;
  /** Whether this is a password field with toggle visibility */
  isPassword?: boolean;
  /** Controlled password visibility state (for password fields) */
  showPassword?: boolean;
  /** Handler for password visibility toggle */
  onTogglePassword?: () => void;
  /** Custom hover shadow intensity (default: 0.15) */
  hoverShadowIntensity?: number;
  /** Custom focus shadow intensity (default: 0.25) */
  focusShadowIntensity?: number;
}

/**
 * StyledTextField Component
 *
 * Reusable text field with consistent styling across the application.
 * Includes hover effects, focus shadows, and optional icon adornments.
 *
 * Features:
 * - Consistent border radius and transitions
 * - Purple-themed hover and focus effects
 * - Optional start/end icons
 * - Built-in password visibility toggle
 * - Customizable shadow intensities
 *
 * @example
 * // Basic usage
 * <StyledTextField
 *   label="Email"
 *   startIcon={<Email />}
 * />
 *
 * @example
 * // Password field with toggle
 * <StyledTextField
 *   label="Password"
 *   isPassword
 *   showPassword={showPassword}
 *   onTogglePassword={() => setShowPassword(!showPassword)}
 *   startIcon={<Lock />}
 * />
 */
const StyledTextField: React.FC<StyledTextFieldProps> = ({
  startIcon,
  endIcon,
  isPassword = false,
  showPassword = false,
  onTogglePassword,
  hoverShadowIntensity = 0.15,
  focusShadowIntensity = 0.25,
  InputProps,
  sx,
  type,
  ...rest
}) => {
  // Determine the input type
  const inputType = isPassword ? (showPassword ? "text" : "password") : type;

  // Build InputProps with icons
  const inputPropsWithIcons = {
    ...InputProps,
    startAdornment: startIcon ? (
      <InputAdornment position="start">{startIcon}</InputAdornment>
    ) : (
      InputProps?.startAdornment
    ),
    endAdornment:
      isPassword && onTogglePassword ? (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle password visibility"
            onClick={onTogglePassword}
            edge="end"
          >
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ) : endIcon ? (
        <InputAdornment position="end">{endIcon}</InputAdornment>
      ) : (
        InputProps?.endAdornment
      ),
  };

  return (
    <TextField
      type={inputType}
      InputProps={inputPropsWithIcons}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "& fieldset": {
            borderColor: "rgba(26,35,126,0.12)",
          },
          "&:hover": {
            boxShadow: `0 4px 16px rgba(26,35,126,${hoverShadowIntensity})`,
          },
          "&:hover fieldset": {
            borderColor: "primary.main",
          },
          "&.Mui-focused": {
            boxShadow: `0 4px 20px rgba(26,35,126,${focusShadowIntensity})`,
            "& fieldset": {
              borderColor: "primary.main",
              borderWidth: "2px",
            },
          },
        },
        "& .MuiInputLabel-root": {
          fontWeight: 500,
          "&.Mui-focused": {
            color: "primary.main",
            fontWeight: 600,
          },
        },
        ...sx,
      }}
      {...rest}
    />
  );
};

export default StyledTextField;
